/**
 * <shared-wishlist> — the page a wishlist share link opens.
 *
 * The wishlist app builds share links against the list detail page path configured in
 * snippets/swish-assets.liquid, and puts the list's public id in a `public-list-id` query
 * parameter. This component reads that id, asks the app for the list's items, and renders
 * the products as this theme's own cards.
 *
 * Read-only by design: the visitor may not own the list, so there is no rename, remove,
 * list switch or list create here. The cards keep their normal save heart, so a visitor can
 * save a product to a list of their own.
 *
 * Cards come from sections/shared-wishlist-cards.liquid through storefront Section
 * Rendering — the same pipeline assets/account-wishlists.js uses — because rendering them in
 * Liquid is what keeps prices and rewards correct for whoever is looking at the page.
 */

/** The app's own query parameter on a share link. */
const LIST_ID_PARAM = 'public-list-id';

class SharedWishlist extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#load(this.#abort.signal).catch((error) => {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#setUnavailable();
      console.debug?.('[swish] shared list failed:', error instanceof Error ? error.message : String(error));
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {AbortSignal} signal */
  async #load(signal) {
    const listId = new URLSearchParams(location.search).get(LIST_ID_PARAM);
    if (!listId) return this.#setUnavailable();

    const { getSharedList } = await import('@theme/swish-client');
    const { name, access, items } = await getSharedList(listId, signal);
    if (signal.aborted) return;

    // Only treat access as decisive when it was actually read: the detail call is allowed
    // to fail without taking the rest of the page down with it.
    if (access && access !== 'public') return this.#setUnavailable();

    if (name) {
      const heading = this.querySelector('.shared-wl__heading');
      if (heading) heading.textContent = name;
    }

    const productIds = [...new Set(items.map((item) => item?.productId).filter(Boolean))].map(String);
    if (!productIds.length) return this.#setEmpty();

    const cards = await this.#fetchCards(productIds, signal);
    if (signal.aborted) return;
    if (!cards.length) return this.#setEmpty();

    const track = this.querySelector('[data-swl-track]');
    if (!track) return;
    track.innerHTML = cards.join('');
    this.dataset.state = 'ready';
  }

  /**
   * One Section Rendering request for the whole list, not one per product: the search
   * endpoint takes every id at once and the section returns all the cards together.
   * @param {string[]} productIds
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>} Card markup, in the order the list holds the products.
   */
  async #fetchCards(productIds, signal) {
    const searchUrl = this.dataset.searchUrl;
    const sectionId = this.dataset.sectionId;
    if (!searchUrl || !sectionId) return [];

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    const html = await fetch(url, { signal })
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '');
    if (signal.aborted || !html) return [];

    const wrapper = new DOMParser().parseFromString(html, 'text/html').getElementById(sectionId);
    if (!wrapper) return [];

    const byId = new Map();
    for (const card of wrapper.children) {
      const id = card.getAttribute('data-product-id');
      if (id) byId.set(id, card.outerHTML);
    }
    // Products the search leaves out — unavailable, unpublished, or in another market —
    // simply do not appear, rather than leaving a hole in the grid.
    return productIds.filter((id) => byId.has(id)).map((id) => byId.get(id));
  }

  #setEmpty() {
    this.#showMessage(this.dataset.messageEmpty, 'empty');
  }

  /** Covers a link with no id, a list that was made private again, and an app outage. */
  #setUnavailable() {
    this.#showMessage(this.dataset.messageUnavailable, 'unavailable');
  }

  /** @param {string | undefined} text @param {string} state */
  #showMessage(text, state) {
    const message = this.querySelector('[data-swl-message]');
    if (message instanceof HTMLElement) {
      message.textContent = text ?? '';
      message.hidden = false;
    }
    this.dataset.state = state;
  }
}

if (!customElements.get('shared-wishlist')) {
  customElements.define('shared-wishlist', SharedWishlist);
}
